/**
 * computeGrade – Single source of truth for percent → letter grade mapping.
 *
 * Uses the Conestoga College "Grading Appendix A" scales.
 * Every UI, report, export, and storage path MUST call this function.
 *
 * @module utils/computeGrade
 */

/**
 * Conestoga Grading Scales keyed by canonical program-type group.
 *
 * Degree / Graduate Certificate – NO "D" grade; F starts at 59.
 * Diploma / Certificate / Advanced Diploma – includes "D" 55-59; F starts at 54.
 */
const CONESTOGA_SCALES = {
  degree: [
    { letter: 'A+', gpa: 4.0,  low: 90, high: 100 },
    { letter: 'A',  gpa: 3.75, low: 80, high: 89  },
    { letter: 'B+', gpa: 3.5,  low: 75, high: 79  },
    { letter: 'B',  gpa: 3.0,  low: 70, high: 74  },
    { letter: 'C+', gpa: 2.5,  low: 65, high: 69  },
    { letter: 'C',  gpa: 2.0,  low: 60, high: 64  },
    { letter: 'F',  gpa: 0.0,  low: 0,  high: 59  },
  ],
  diploma: [
    { letter: 'A+', gpa: 4.0,  low: 90, high: 100 },
    { letter: 'A',  gpa: 3.75, low: 80, high: 89  },
    { letter: 'B+', gpa: 3.5,  low: 75, high: 79  },
    { letter: 'B',  gpa: 3.0,  low: 70, high: 74  },
    { letter: 'C+', gpa: 2.5,  low: 65, high: 69  },
    { letter: 'C',  gpa: 2.0,  low: 60, high: 64  },
    { letter: 'D',  gpa: 1.0,  low: 55, high: 59  },
    { letter: 'F',  gpa: 0.0,  low: 0,  high: 54  },
  ],
};

/**
 * Map a human-readable program type string to a scale key.
 * "Degree Programs", "Degree", "Graduate Certificate" → "degree"
 * Everything else → "diploma"
 */
function resolveScaleKey(programType) {
  if (!programType) return 'diploma';

  const normalized = programType.toString().trim().toLowerCase();

  // Degree-family matches
  if (
    normalized === 'degree' ||
    normalized === 'degree programs' ||
    normalized === 'graduate certificate' ||
    normalized === 'grad'
  ) {
    return 'degree';
  }

  return 'diploma';
}

/**
 * Compute the letter grade, GPA, and range boundaries for a given percentage
 * and program type.
 *
 * @param {Object}  params
 * @param {string}  params.programType  – e.g. "Degree", "Diploma", "Certificate",
 *                                         "Graduate Certificate", "Advanced Diploma"
 * @param {number}  params.percent      – 0-100 numeric percentage
 * @returns {{ letter: string, gpa: number, rangeLow: number, rangeHigh: number }}
 */
export function computeGrade({ programType, percent }) {
  const key = resolveScaleKey(programType);
  const scale = CONESTOGA_SCALES[key];

  // Clamp to valid range
  const pct = typeof percent === 'number' ? Math.max(0, Math.min(100, Math.round(percent))) : 0;

  for (const row of scale) {
    if (pct >= row.low && pct <= row.high) {
      return {
        letter: row.letter,
        gpa: row.gpa,
        rangeLow: row.low,
        rangeHigh: row.high,
      };
    }
  }

  // Should never happen with valid scales covering 0-100, but safety fallback
  return { letter: 'F', gpa: 0.0, rangeLow: 0, rangeHigh: 59 };
}

/**
 * Return the full scale array for a given program type (useful for UI display).
 */
export function getScaleForProgramType(programType) {
  const key = resolveScaleKey(programType);
  return CONESTOGA_SCALES[key];
}

/**
 * Convenience: just get the letter grade string.
 */
export function getLetterGradeForProgram(percent, programType) {
  return computeGrade({ programType, percent }).letter;
}

/**
 * Export the scale key resolver for tests and other utilities.
 */
export { resolveScaleKey, CONESTOGA_SCALES };
